import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import ProfileCard from './ProfileCard';
import StatsCard from './StatsCard';
import TeamCard from './TeamCard';
import QuickActionsBar from './QuickActionsBar';
import CommunityCanvas from '../components/CommunityCanvas';
import ScheduleCard from '../components/ScheduleCard';
import ConflictBanner from '../components/ConflictBanner';
import CollectionBanner from '../components/CollectionBanner';
import AnnouncementBanner from '../components/AnnouncementBanner';
import BirthdayCardModal from '../components/BirthdayCardModal';
// PerformanceCharts removed: the trends section has been deprecated per user request.

function TherapistDashboard() {
  const navigate = useNavigate();

  // THERAPIST STATS
  const [sessionStats, setSessionStats] = useState({
    lastWeekCount: 0,
    thisWeekCount: 0,
    monthlyAvgCount: 0,
    debtCount: 0,
    debtTotal: 0,
    noShowWeekCount: 0,
    noShowMonthCount: 0,
    cancelWeekCount: 0,
    cancelMonthCount: 0
  });

  // GOALS
  const [goals, setGoals] = useState({
    weekly_session_goal: 0,
    weekly_session_bonus: 0
  });

  // THERAPIST NAME
  const [therapistName, setTherapistName] = useState('');

  // THERAPIST PROFILE
  const [profile, setProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [cardTarget, setCardTarget] = useState(null);

  // TEAM MEMBERS
  const [teamMembers, setTeamMembers] = useState([]);

  // Unread admin messages count
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch the unread messages count for this therapist
  const fetchUnreadCount = async () => {
    try {
      const res = await API.get('/therapist/messages/unread_count');
      const count = res.data && typeof res.data.count === 'number' ? res.data.count : 0;
      setUnreadCount(count);
    } catch (err) {
      console.error('Error fetching unread message count:', err);
    }
  };

  // The trends / performance charts section has been removed as it
  // provided limited value and has been deprecated.  If you need to
  // reintroduce charts in the future, you can create a dedicated
  // component and fetch data as needed.

  // --------------------------------------------------------------------------
  // FETCH STATS & GOALS
  // --------------------------------------------------------------------------
  const fetchSessionStats = async () => {
    try {
      const res = await API.get('/therapist/stats');
      const data = res.data || {};
      data.debtTotal = parseFloat(data.debtTotal) || 0;
      setSessionStats(data);
    } catch (error) {
      console.error('Error fetching session stats:', error);
    }
  };

  const fetchGoals = async () => {
    try {
      const res = await API.get('/therapist/goals');
      setGoals(res.data || {});
    } catch (err) {
      console.error('Error fetching therapist goals:', err);
    }
  };

  // --------------------------------------------------------------------------
  // LOAD THERAPIST NAME FROM LOCALSTORAGE
  // --------------------------------------------------------------------------
  const loadTherapistName = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setTherapistName(`${user.first_name} ${user.last_name}` || '');
      setCurrentUserId(user.id || null);
    }
  };

  // --------------------------------------------------------------------------
  // FETCH THERAPIST PROFILE
  // --------------------------------------------------------------------------
  const fetchTherapistProfile = async () => {
    try {
      const res = await API.get('/therapist/profile');
      setProfile(res.data);
    } catch (error) {
      console.error('Error fetching therapist profile:', error);
    }
  };
  
  // --------------------------------------------------------------------------
  // FETCH TEAM MEMBERS
  // --------------------------------------------------------------------------
  const fetchTeamMembers = async () => {
    try {
      const res = await API.get('/therapist/team-profiles');
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // --------------------------------------------------------------------------
  // NEW CLIENT PROMPTS (Acuity confirmed, no matching portal client yet)
  // --------------------------------------------------------------------------
  const [newClientPrompts, setNewClientPrompts] = useState([]);
  const [loadingPromptId, setLoadingPromptId] = useState(null);

  useEffect(() => {
    let active = true;
    API.get('/therapist/new-client-prompts')
      .then((res) => { if (active) setNewClientPrompts(res.data || []); })
      .catch(() => { /* non-fatal: banner just won't show */ });
    return () => { active = false; };
  }, []);

  const dismissPrompt = async (acuityId) => {
    setNewClientPrompts((prev) => prev.filter((p) => p.acuityId !== acuityId));
    try { await API.post(`/therapist/new-client-prompts/${acuityId}/dismiss`); }
    catch (_) { /* best-effort; reappears next load if it failed */ }
  };

  // CLIENT COMPLETED A LINK (intake signature or ROI)
  const [newCompletions, setNewCompletions] = useState([]);

  useEffect(() => {
    let active = true;
    API.get('/therapist/new-completions')
      .then((res) => { if (active && res.data) setNewCompletions(res.data); })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  const dismissCompletion = async (c) => {
    setNewCompletions((prev) => prev.filter((x) => !(x.linkType === c.linkType && x.tokenId === c.tokenId)));
    try { await API.post('/therapist/new-completions/dismiss', { linkType: c.linkType, tokenId: c.tokenId }); }
    catch (_) { /* best-effort */ }
  };

  // --------------------------------------------------------------------------
  // DOUBLE-BOOKING CONFLICTS
  // --------------------------------------------------------------------------
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    let active = true;
    API.get('/acuity/my-conflicts')
      .then((res) => { if (active && res.data && !res.data.noCalendar) setConflicts(res.data.conflicts || []); })
      .catch(() => { /* non-fatal: banner just won't show */ });
    return () => { active = false; };
  }, []);

  const clearConflict = async (conflict, mode) => {
    const matchKey = mode === 'dismiss_once' ? conflict.signature : conflict.entityKey;
    setConflicts((prev) => prev.filter((c) => {
      if (mode === 'dismiss_once') return c.signature !== conflict.signature;
      return !(c.kind === conflict.kind && c.entityKey === conflict.entityKey);
    }));
    try {
      await API.post('/acuity/conflicts/clear', { mode, conflictKind: conflict.kind, matchKey });
    } catch (_) { /* best-effort; reappears next load if it failed */ }
  };

  const startCreateFromPrompt = async (p) => {
    setLoadingPromptId(p.acuityId);
    // Basic prefill always works; enrich with the full Acuity intake when available.
    let prefill = { firstName: p.firstName, lastName: p.lastName, email: p.email, phone: p.phone };
    try {
      const res = await API.get(`/therapist/new-client-prompts/${p.acuityId}/prefill`);
      if (res.data && typeof res.data === 'object') prefill = res.data;
    } catch (_) { /* fall back to basic prefill */ }
    setLoadingPromptId(null);
    navigate('/therapist/client-profile', { state: { prefill } });
  };

  // --------------------------------------------------------------------------
  // useEffect (on mount)
  // --------------------------------------------------------------------------
  useEffect(() => {
    fetchSessionStats();
    fetchGoals();
    loadTherapistName();
    fetchTherapistProfile();
    fetchTeamMembers();
    fetchUnreadCount();
    // In a real app, we would also fetch trend data here
  }, []);

  // --------------------------------------------------------------------------
  // GREETING
  // --------------------------------------------------------------------------
  function getTimeOfDayGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }
  const greeting = `${getTimeOfDayGreeting()}, ${therapistName}!`;

  // --------------------------------------------------------------------------
  // TENURE AND CELEBRATION HELPERS
  // These helpers compute the tenure (years at practice based on hire_date)
  // and identify birthdays and work anniversaries occurring within the
  // current week.  They are memoized to avoid recalculating on every render.

  // Calculate years since a given date string. Returns null if date is empty or invalid.
  const yearsSince = (dateStr, onDate = new Date()) => {
    if (!dateStr) return null;
    const dateOnly = String(dateStr).split('T')[0].split('-').map(Number);
    if (dateOnly.length < 3 || dateOnly.some((n) => Number.isNaN(n))) return null;
    const d = new Date(dateOnly[0], dateOnly[1] - 1, dateOnly[2]);
    if (isNaN(d)) return null;
    let years = onDate.getFullYear() - d.getFullYear();
    const hadAnniv =
      onDate.getMonth() > d.getMonth() ||
      (onDate.getMonth() === d.getMonth() && onDate.getDate() >= d.getDate());
    if (!hadAnniv) years -= 1;
    return Math.max(years, 0);
  };

  // Compute tenure for the logged-in therapist (derived from hire_date)
  const tenureYears = useMemo(() => yearsSince(profile?.hire_date || null), [profile?.hire_date]);

  // Helpers to determine the start and end of the current week (Monday to Sunday)
  const startOfWeek = (d = new Date()) => {
    const date = new Date(d);
    const day = date.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
    // Determine difference to Monday (or previous Monday if today is Sunday)
    const diff = (day === 0 ? -6 : 1) - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };
  const endOfWeek = (d = new Date()) => {
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(e.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
  };

  // Given a date string (YYYY-MM-DD), return the date object representing
  // the same month/day in the current year. Handles Feb 29 on non-leap years by
  // mapping to Feb 28.
  const sameMonthDayThisYear = (dateStr) => {
    if (!dateStr) return null;
    const dateOnly = String(dateStr).split('T')[0].split('-').map(Number);
    if (dateOnly.length < 3 || dateOnly.some((n) => Number.isNaN(n))) return null;
    const d = new Date(dateOnly[0], dateOnly[1] - 1, dateOnly[2]);
    if (isNaN(d)) return null;
    const now = new Date();
    let candidate = new Date(now.getFullYear(), d.getMonth(), d.getDate());
    // If Feb 29 and non-leap year, adjust to Feb 28
    if (d.getMonth() === 1 && d.getDate() === 29 && candidate.getMonth() !== 1) {
      candidate = new Date(now.getFullYear(), 1, 28);
    }
    return candidate;
  };

  // Check whether a given date falls within a week range
  const inWeek = (dateObj, weekStart, weekEnd) => {
    if (!dateObj) return false;
    return dateObj >= weekStart && dateObj <= weekEnd;
  };

  // Build a list of celebration items for the current week.
  // Each item includes the event type ('birthday' or 'anniversary'), the date of
  // the occurrence, the name of the teammate, the number of years for
  // anniversaries, and an optional photo URL.
  const celebrationItems = useMemo(() => {
    const s = startOfWeek();
    const e = endOfWeek();
    return (teamMembers || []).flatMap((m) => {
      const items = [];
      // Birthday logic: check if teammate's dob occurs this week
      const bd = sameMonthDayThisYear(m?.dob);
      if (inWeek(bd, s, e)) {
        items.push({
          type: 'birthday',
          date: bd,
          name: `${m.first_name} ${m.last_name}`,
          years: null,
          photo: m.photo_url || null,
          userId: m.therapist_id,
          email: m.email,
        });
      }
      // Work anniversary logic: check if hire_date anniversary occurs this week
      const hd = sameMonthDayThisYear(m?.hire_date);
      if (inWeek(hd, s, e)) {
        items.push({
          type: 'anniversary',
          date: hd,
          name: `${m.first_name} ${m.last_name}`,
          years: yearsSince(m?.hire_date, new Date()),
          photo: m.photo_url || null,
          userId: m.therapist_id,
          email: m.email,
        });
      }
      return items;
    });
  }, [teamMembers]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* HEADER WITH GREETING */}
      <div style={headerStyle}>
        <h2 style={{ margin: 0 }}>{greeting}</h2>
        {unreadCount > 0 && (
          <span
            style={{
              backgroundColor: 'var(--error-color)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              marginLeft: '8px',
              fontSize: '0.85rem'
            }}
          >
            {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <AnnouncementBanner audience="therapist" />

      {/* CLIENT COMPLETED A LINK: intake signature or ROI */}
      <CollectionBanner icon="📝" collections={newCompletions} onDismiss={dismissCompletion} />

      {/* NEW CLIENT PROMPTS: banner for Acuity-confirmed clients not yet in the portal */}
      {newClientPrompts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {newClientPrompts.map((p) => {
            const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
            const initials = `${(p.firstName || '').charAt(0)}${(p.lastName || '').charAt(0)}`.toUpperCase();
            const isLoading = loadingPromptId === p.acuityId;
            return (
              <div
                key={p.acuityId}
                style={{
                  position: 'relative', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                  background: 'linear-gradient(135deg, var(--primary-color) 0%, #3A4F9E 55%, #5C7BD6 100%)',
                  color: '#fff', borderRadius: 14, padding: '16px 20px', marginBottom: 12,
                  boxShadow: '0 6px 20px rgba(29,44,95,0.28)',
                }}
              >
                <span style={{ position: 'absolute', top: 8, right: 16, fontSize: 18, opacity: 0.9 }}>✨</span>
                <span style={{ position: 'absolute', bottom: 8, right: 64, fontSize: 12, opacity: 0.5 }}>✨</span>

                <div
                  style={{
                    flexShrink: 0, width: 52, height: 52, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)', border: '2px solid rgba(255,255,255,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 18, letterSpacing: 0.5,
                  }}
                >
                  {initials || '🙂'}
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.85 }}>
                    🆕 New client confirmed
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2, marginTop: 2 }}>
                    {fullName || 'New client'}
                  </div>
                  {p.apptDatetime && (
                    <span
                      style={{
                        display: 'inline-block', marginTop: 6, padding: '2px 10px', borderRadius: 999,
                        background: 'rgba(255,255,255,0.18)', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      📅 {new Date(p.apptDatetime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    onClick={() => startCreateFromPrompt(p)}
                    disabled={isLoading}
                    style={{
                      background: '#fff', color: 'var(--primary-color)', border: 'none', borderRadius: 8,
                      padding: '10px 18px', fontWeight: 700, fontSize: 14,
                      cursor: isLoading ? 'default' : 'pointer', opacity: isLoading ? 0.7 : 1,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)', whiteSpace: 'nowrap',
                    }}
                  >
                    {isLoading ? 'Loading…' : 'Create client →'}
                  </button>
                  <button
                    onClick={() => dismissPrompt(p.acuityId)}
                    disabled={isLoading}
                    style={{
                      background: 'transparent', color: 'rgba(255,255,255,0.85)',
                      border: '1px solid rgba(255,255,255,0.5)', borderRadius: 8,
                      padding: '10px 14px', fontWeight: 600, fontSize: 14,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DOUBLE-BOOKING WARNINGS */}
      <ConflictBanner conflicts={conflicts} onClear={clearConflict} />

      {/* CELEBRATION CARD: appears only when there are birthdays or work anniversaries this week */}
      {celebrationItems.length > 0 && (
        <CelebrationCard
          items={celebrationItems}
          currentUserId={currentUserId}
          onDrawCard={(item) => setCardTarget(item)}
        />
      )}

      {/* TOP ROW - PROFILE, STATS, TEAM */}
      <div style={topRowStyle}>
        {/* PROFILE CARD */}
        <ProfileCard 
          profile={{
            ...profile,
            // Pass derived tenure years to profile for display
            tenure_years: tenureYears,
          }}
          sessionStats={sessionStats}
          goals={goals}
        />

        {/* STATS CARD */}
        <StatsCard 
          sessionStats={sessionStats}
          navigate={navigate}
        />

        {/* TEAM CARD - Using existing component */}
        <TeamCard 
          teamMembers={teamMembers}
        />
      </div>

      {/* MIDDLE ROW - QUICK ACTIONS */}
      <div style={middleRowStyle}>
        <QuickActionsBar
          navigate={navigate}
        />
      </div>

      {/* SCHEDULE */}
      <ScheduleCard />

      {/* COMMUNITY CANVAS */}
      <CommunityCanvas />

      {cardTarget && (
        <BirthdayCardModal
          recipientId={cardTarget.userId}
          recipientFirstName={cardTarget.name.split(' ')[0]}
          type={cardTarget.type}
          onClose={() => setCardTarget(null)}
        />
      )}
    </div>
  );
}

// STYLES
const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
};

const topRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  marginBottom: '24px'
};

const middleRowStyle = {
  marginBottom: '24px'
};

const chartsContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  border: '1px solid var(--border-color)',
  marginBottom: '24px'
};

// --------------------------------------------------------------------------
// CelebrationCard Component
// This component renders a celebratory card for birthdays and work anniversaries
// occurring within the current week. It accepts a list of items, where each
// item specifies the type ('birthday' or 'anniversary'), the person's name,
// an optional number of years for anniversaries, and an optional photo URL. If
// a photo is not provided, the card displays the person's initials instead.
function CelebrationCard({ items, currentUserId, onDrawCard }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #fce7f3 0%, #dbf4ff 100%)',
        border: '1px solid var(--border-color)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 24,
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>🎉 Celebrations This Week</h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 12,
        }}
      >
        {items.map((it, idx) => (
          <div
            key={idx}
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px solid var(--border-color)',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                overflow: 'hidden',
                background: 'var(--surface-muted)',
                flexShrink: 0,
              }}
            >
              {it.photo ? (
                <img
                  src={it.photo}
                  alt={it.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                  }}
                >
                  {it.name
                    .split(' ')
                    .map((s) => s[0])
                    .join('')
                    .slice(0, 2)}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>
                {it.type === 'birthday'
                  ? `🎂 Happy Birthday, ${it.name}!`
                  : `🎉 Work Anniversary — ${it.name}`}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-strong)' }}>
                {it.type === 'birthday'
                  ? (it.date
                      ? it.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                      : 'This week')
                  : (typeof it.years === 'number'
                      ? `${
                          it.years === 0
                            ? 'First year!'
                            : `${it.years} year${it.years === 1 ? '' : 's'}`
                        } at the practice`
                      : 'This week')}
              </div>
              {Number(it.userId) !== Number(currentUserId) && onDrawCard && (
                <button
                  onClick={() => onDrawCard(it)}
                  style={{
                    marginTop: 8, padding: '4px 12px', borderRadius: 6, fontSize: 12,
                    background: 'var(--primary-color)', color: '#fff', border: 'none',
                    cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  🎨 Draw a card
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TherapistDashboard;